import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { CustomerLocationStore, CustomerStore, ICustomerLocation } from '@engineering11/customer-web'
import { E11ModalGlobalService } from '@engineering11/ui-lib/e11-modal'
import { filterByKeys, isNotNil } from '@engineering11/utility'
import { combineLatest, filter, map, startWith } from 'rxjs'

/** What sdk-form-location emits: id and customerKey are absent on a new location. */
type EditableLocation = Omit<ICustomerLocation, 'id' | 'customerKey'> & Partial<Pick<ICustomerLocation, 'id' | 'customerKey'>>

/** The SDK list's search, unchanged: these keys, fuzzy-matched, from two characters. */
const SEARCH_KEYS = ['name', 'address', 'address2', 'city', 'state', 'zip', 'countryCode'] as const

/**
 * Renders the location list itself rather than through the SDK's sdk-customer-locations, whose cards clamped the whole body
 * to two lines inside a fixed height. A table matching Manage Openings; the SDK's store and edit form are still used.
 */
@Component({
  template: `
    <header class="settings-header">
      <div class="settings-header-main">
        <h2 class="settings-title">{{ 'Manage the locations within your company' | translate }}</h2>
        <p class="settings-subtitle">{{ 'These are used to assign specific locations to openings.' | translate }}</p>
      </div>
      <div class="settings-actions">
        <e11-button
          id="submit_account"
          value="{{ 'New Location' | translate }}"
          [icon]="'add'"
          [iconPosition]="'left'"
          [iconSize]="'md-18'"
          [size]="'sm'"
          [color]="'primary-accent'"
          [type]="'button'"
          (click)="addClicked()"
        >
        </e11-button>
      </div>
    </header>

    <div class="e11-mb-6">
      <e11-loader-dots *ngIf="(loaded$ | async) === false; else locationList" [size]="'md'" [color]="'e11-bg-skin-primary-accent'"></e11-loader-dots>

      <ng-template #locationList>
        <div class="settings-search">
          <span class="material-icons-outlined settings-search-icon" aria-hidden="true">search</span>
          <input
            id="input_searchForm"
            type="search"
            class="settings-search-input"
            autocomplete="off"
            [formControl]="searchForm.controls.search"
            [placeholder]="'Search' | translate"
            [attr.aria-label]="'Search' | translate"
          />
          <button
            *ngIf="searchForm.value.search"
            type="button"
            class="settings-search-clear"
            [attr.aria-label]="'Clear search' | translate"
            (click)="searchForm.controls.search.setValue('')"
          >
            <span class="material-icons-outlined" aria-hidden="true">close</span>
          </button>
        </div>

        <ng-container *ngrxLet="filteredLocations$ as locations">
          <div *ngIf="locations.length; else emptyState" class="settings-table-wrap">
            <table class="settings-table">
              <thead>
                <tr>
                  <th scope="col" class="col-location">{{ 'Location' | translate }}</th>
                  <th scope="col" class="col-address">{{ 'Address' | translate }}</th>
                  <th scope="col" class="col-fit">{{ 'Phone' | translate }}</th>
                  <th scope="col" class="col-fit">{{ 'Status' | translate }}</th>
                  <th scope="col" class="col-fit">
                    <span class="e11-sr-only">{{ 'Edit' | translate }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let location of locations; trackBy: trackById" [class.is-inactive]="!location.active" (click)="select(location)">
                  <td class="col-location">
                    <div class="row-name">{{ location.name }}</div>
                    <div *ngIf="location.secondaryName || location.internalCode" class="row-sub">
                      <span *ngIf="location.secondaryName">{{ location.secondaryName }}</span>
                      <span *ngIf="location.internalCode" class="row-code">{{ location.internalCode }}</span>
                    </div>
                  </td>
                  <td class="col-address">{{ addressLine(location) || '—' }}</td>
                  <td class="col-fit">{{ location.phone || '—' }}</td>
                  <td class="col-fit">
                    <span
                      class="status-pill"
                      [class.is-active]="location.active"
                      [title]="
                        (location.active
                          ? 'This location is active and available for selection.'
                          : 'This location is disabled and is not available for selection. Re-enable this location to make it an available selection.'
                        ) | translate
                      "
                      ><i class="dot"></i>{{ (location.active ? 'Active' : 'Inactive') | translate }}</span
                    >
                  </td>
                  <td class="col-fit actions-cell">
                    <span class="material-icons-outlined edit-icon" aria-hidden="true">edit</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>

        <ng-template #emptyState>
          <e11-empty-state
            [title]="'No Locations' | translate"
            [clickable]="true"
            [subtitle]="'It appears that there are no locations entered yet.' | translate"
            [icon]="''"
            [border]="'solid'"
            (click)="addClicked()"
          ></e11-empty-state>
        </ng-template>
      </ng-template>
    </div>

    <ng-template #editLocation>
      <div *ngrxLet="currentCustomer$ as currentCustomer">
        <sdk-form-location
          [location]="(selectedLocation$ | async) ?? undefined"
          [activeInfo]="locationActiveInfo"
          (cancelled)="deselect()"
          (locationAdded)="add($event, currentCustomer.id)"
          (locationDeleted)="delete($event)"
          (locationUpdated)="update($event)"
        ></sdk-form-location>
      </div>
    </ng-template>
  `,
  styleUrls: ['./locations.component.scss'],
})
export class CustomerLocationsComponent implements OnInit {
  @ViewChild('editLocation') editLocationModal!: TemplateRef<unknown>

  locationActiveInfo =
    "The status here allows this 'Location' to be available for Opening and Template Listings. (as well as in other places). If you turn it off it will not show as a selection in screens that let you choose a location"

  searchForm = new FormGroup({ search: new FormControl('') })

  currentCustomer$ = this.customerStore.currentCustomer$.pipe(filter(isNotNil))
  loaded$ = this.locationStore.loaded$
  selectedLocation$ = this.locationStore.selectedItem$

  filteredLocations$ = combineLatest([
    this.locationStore.allItems$,
    this.searchForm.controls.search.valueChanges.pipe(startWith(this.searchForm.controls.search.value)),
  ]).pipe(map(([locations, search]) => filterLocations(locations, search)))

  constructor(private customerStore: CustomerStore, private locationStore: CustomerLocationStore, private modalService: E11ModalGlobalService) {}

  ngOnInit() {
    this.locationStore.loadAllItems(this.currentCustomer$.pipe(map(customer => customer.id)))
  }

  /** One line — street, unit, city, region and country joined, wrapping only where the column runs out of room. */
  addressLine({ address, address2, city, state, zip, countryCode }: ICustomerLocation): string {
    const region = [state, zip].filter(Boolean).join(' ')
    return [address, address2, city, region, countryCode?.toUpperCase()].filter(part => !!part?.trim()).join(', ')
  }

  trackById(_: number, location: ICustomerLocation) {
    return location.id
  }

  addClicked() {
    this.locationStore.selectItem(undefined)
    this.openModal()
  }

  select(location: ICustomerLocation) {
    this.locationStore.selectItem(location)
    this.openModal()
  }

  deselect() {
    this.locationStore.selectItem(undefined)
    this.modalService.closeModal()
  }

  add(location: EditableLocation, customerKey: string) {
    this.locationStore.add({ ...location, customerKey } as ICustomerLocation)
    this.deselect()
  }

  update(location: EditableLocation) {
    this.locationStore.update(location as ICustomerLocation)
    this.deselect()
  }

  delete(location: EditableLocation) {
    this.locationStore.delete((location as ICustomerLocation).id)
    this.deselect()
  }

  private openModal() {
    this.modalService.openModal({
      body: this.editLocationModal,
      width: 'lg',
      darkMode: false,
      title: 'Edit Location',
      showHeader: true,
      showFooter: false,
      hideCloseButton: false,
    })
  }
}

function filterLocations(locations: ICustomerLocation[], search: string | null): ICustomerLocation[] {
  if (!search || search.length < 2) return locations
  return filterByKeys(search, locations, [...SEARCH_KEYS])
}
